import { formatValidQueryParamUrlCommaSeparated, isBlank } from "utils/common";
import { formatSale } from "utils/sales.util";
import { createPaginatedEndpoint } from "utils/services.util";

import { emptySplitApi } from "./empty-split-api";
import {
  HCFAAttachmentInput,
  HCFAForm,
  HCFAFormInput
} from "./types/hcfa1500-forms.type";
import { InvoiceHistoryEvent } from "./types/invoice-history.type";
import { CSVReportResponse } from "./types/reports.type";
import { SaleItem } from "./types/sale-items.type";
import {
  AccountReceivableSalesResponse,
  ConvertQuoteSaleInput,
  CreateRefitInvoiceInput,
  CreateRefitInvoiceResponse,
  ItemInput,
  LineItem,
  PatientSalesFilters,
  Sale,
  SaleInput,
  SalesDownloadQuery,
  SalesFilters,
  SalesReportResponse,
  SalesResponse,
  type SalesTotalResponse
} from "./types/sales.type";

export const DEFAULT_SALES_LIMIT = 25;

export const SALES_DEFAULT_QUERY =
  "{id,balance,is_active_subscription,created_by,items{cpt_code,insurer_responsibility},patient{id,first_name,middle_name,last_name,preferred_name,title,suffix,quickbooks_customer_id},provider{id,first_name,last_name,title,suffix,is_active},is_direct_supervisor,supervising_provider{id,first_name,last_name,title,suffix,is_active},created_at,invoice_date,service_date,status,total,total_adjusted,total_paid,most_recent_hcfa_1500_form{id,status,form_data,is_uploaded},appointment{id,chart_note{icd10_codes}},quickbooks_invoice_id,quickbooks_sync{sync_status,error},clinic{id,name,logo,practice{id,name,logo}},patient_responsibility_balance,insurer_responsibility_balance,total_refunds,payment_source_type,managed_care_plan,insurance_policy{id,insurer{id,name,attachments_allowed}},secondary_insurance_policy{id,insurer{id,name,attachments_allowed}},assigned_tags{id,tag,description,expires_at,created_by,updated_by,created_at,updated_at},is_migrated,migration_source_id,is_source_viewable,collection{id,status,original_balance,current_balance}}";

/**
 * DEV-3169. Adds the refit re-billing read keys to a RestQL sale query, but ONLY when the
 * `refitRebilling` flag is on.
 *
 * The gate is the keys, not the render. django-restql raises `FieldNotFound` for a key the
 * serializer does not declare, which 400s the WHOLE sale GET — so an ungated frontend blanks the
 * invoice list against any backend that predates the fields, not just the chips. Backend ships
 * first; this stays gated until every environment has it.
 *
 * A composer rather than a second copy of each query constant: there are four sale queries and
 * duplicating them would be eight, with nothing keeping the pairs in step.
 */
// `refit_invoice_id` is deliberately NOT here. The other four are plain columns on the invoice row
// and cost nothing to read; that one is a reverse lookup ("has some later invoice re-billed this
// one?") that the database has to answer per row by searching the invoices table, so its cost grows
// with the table while the rest stay flat. It fed only the "RE-BILLED -> #123" badge on the
// original invoice, which is not worth a scaling cost on the patient billing page (DEV-3169).
// The backend still serves it, gated on being asked for, so a surface that genuinely needs the
// back-pointer can request it — just not the invoice list.
export const REFIT_QUERY_FIELDS =
  "rebill_of,rebill_reason,rebill_reason_display,counts_as_sale";

const withTopLevelFields = (
  query: string,
  fields: string,
  enabled?: boolean
): string => {
  if (!enabled) return query;
  if (!query.endsWith("}")) return query;
  return `${query.slice(0, -1)},${fields}}`;
};

export const withRefitFields = (query: string, enabled?: boolean): string =>
  withTopLevelFields(query, REFIT_QUERY_FIELDS, enabled);

/** Adds `qbo_sync_excluded` only while `qboSyncExclusionRules` is on; see `withRefitFields`. */
export const withQboSyncExcludedField = (
  query: string,
  enabled?: boolean
): string => withTopLevelFields(query, "qbo_sync_excluded", enabled);

export const getSalesQuery = (enableDarwillIntegration?: boolean) => {
  if (enableDarwillIntegration) {
    return `${SALES_DEFAULT_QUERY.slice(0, -1)},darwill_invoice_file_url,darwill_sent_date}`;
  }
  return SALES_DEFAULT_QUERY;
};

export const PATIENT_SALES_QUERY =
  "{id,balance,payment_source_type,insurance_policy{id,insurer{id,name,payer_name,attachments_allowed}},secondary_insurance_policy{id,insurer{id,name,payer_name,attachments_allowed}},managed_care_plan{id,name,managed_care_provider{id,name}},provider{id,first_name,last_name,title,suffix,full_name,npi,is_active},created_at,invoice_date,service_date,status,total,total_adjusted,total_paid,most_recent_hcfa_1500_form{id,status,form_data,is_uploaded},appointment{id,type{id,name,default_duration},chart_note{icd10_codes}},quickbooks_invoice_id,quickbooks_sync{sync_status,error},clinic{phone,city,quickbooks_token,review_link,name,card_processing_provider,surcharge_percent,effective_surcharge_percent,justifi_account_id,tilled_account_id,id,state,zip_code,region,country,extra{id,billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_country,billng_zip,billng_phone},quickbooks_realm_id,google_place_id,practice{id,name,logo,card_processing_provider,justifi_account_id,tilled_account_id},is_active,logo,timezone,display_name,sms_phone,street_address_1,street_address_2},patient_responsibility_balance,insurer_responsibility_balance,total_refunds,items{id,cpt_code{id,code},patient_responsibility,total,insurer_responsibility,insurers{id,content_type,object_id,amount}},created_by,updated_by,insurer_responsibility_total,patient_responsibility_total,patient{id,full_name,quickbooks_customer_id},is_migrated}";

const GET_SALE_QUERY =
  "{id,patient{id,full_name,first_name,middle_name,last_name,preferred_name,title,suffix,phone,email,birthdate,quickbooks_customer_id,is_active,street_address_1,street_address_2,city,state,zip_code},service_date,created_at,invoice_date,icd10_codes,appointment{id,type{id,name},start_moment,end_moment,status,chart_note{icd10_codes},timer{id,status,status_display,minutes_accumulated,last_started_at,completed}},provider{title,last_name,is_active,suffix,photo,signature,first_name,role,id,npi,license_number,resolved_license_number,state_licenses{state,license_number}},is_direct_supervisor,supervising_provider{title,last_name,is_active,suffix,photo,signature,first_name,role,id,npi,license_number,resolved_license_number,state_licenses{state,license_number}},clinic{phone,ein,city,quickbooks_token,review_link,name,card_processing_provider,surcharge_percent,effective_surcharge_percent,justifi_account_id,tilled_account_id,id,npi,state,zip_code,region,country,extra{id,billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_country,billng_zip,billng_phone},quickbooks_realm_id,google_place_id,practice{id,name,logo,card_processing_provider,justifi_account_id,tilled_account_id},is_active,logo,timezone,display_name,sms_phone,street_address_1,street_address_2},total,total_paid,total_adjusted,balance,is_balanced,status,notes,invoice_memo,patient_responsibility_total,insurer_responsibility_total,patient_responsibility_total_paid,insurer_responsibility_total_paid,patient_responsibility_total_adjusted,insurer_responsibility_total_adjusted,patient_responsibility_total_refunds,insurer_responsibility_total_refunds,patient_responsibility_balance,insurer_responsibility_balance,patient_responsibility_balance_field,insurer_responsibility_balance_field,quickbooks_invoice_id,quickbooks_sync{sync_status,error},total_refunds,payment_source_type,managed_care_plan{id,name,notes,managed_care_provider{id,name}},insurance_policy{id,policyholder_member_id,plan_name,is_active,type,hearing_benefit,deductible,copay,has_hearing_aid,policyholder_relationship,policyholder_first_name,policyholder_last_name,policyholder_middle_name,policyholder_sex,policyholder_birthdate,policyholder_employer_name,policyholder_group_number,policyholder_street_address_1,policyholder_street_address_2,policyholder_city,policyholder_state,policyholder_country,policyholder_zip_code,policyholder_phone,notes,insurance_card_front,insurance_card_back,insurer_phone_number,coverage_type,insurer{id,name,payer_id,payer_name,attachments_allowed},effective_date,expiration_date,policy_type},secondary_insurance_policy{id,policyholder_member_id,plan_name,is_active,type,hearing_benefit,deductible,copay,has_hearing_aid,policyholder_relationship,policyholder_first_name,policyholder_last_name,policyholder_middle_name,policyholder_sex,policyholder_birthdate,policyholder_employer_name,policyholder_group_number,policyholder_street_address_1,policyholder_street_address_2,policyholder_city,policyholder_state,policyholder_country,policyholder_zip_code,policyholder_phone,notes,insurance_card_front,insurance_card_back,insurer_phone_number,coverage_type,insurer{id,name,payer_id,payer_name,attachments_allowed},effective_date,expiration_date,policy_type},most_recent_hcfa_1500_form{id,status},assigned_tags,items{id,patient_responsibility,total,insurer_responsibility,insurers{id,content_type,object_id,amount}},created_by,refunds{id,payer_type},collection{id,status},collection_reenroll_suggested,is_migrated,is_source_viewable,migration_source_id}";

// upload_response is safe to request unflagged: it is an ancient column (billing migration
// 0016) exposed read-only on the serializer, and the claims tab renders it as a DATED record
// of what Claim.MD said at upload — never as the claim's current status (see billing/CLAUDE.md).
const GET_HCFA1500_FORMS_QUERY =
  "{id,status,created_by,is_assignment_accepted,is_uploaded,uploaded_at,form_data,upload_response,era_claims{id},primary_insurance_policy{id,insurer{id,attachments_allowed}}}";

// resubmission_code / payer_icn / original_form / upload_response and
// era_claims.payer_icn are backend-first keys (Box 22 corrected claims): they 400
// the whole GET (FieldNotFound) against a backend that predates them, so they are
// only requested when CORRECTED_CLAIMS_BOX22_FEATURE is on (callers pass box22Enabled).
const GET_HCFA1500_FORMS_BOX22_QUERY =
  "{id,status,created_by,is_assignment_accepted,is_uploaded,uploaded_at,form_data,era_claims{id,payer_icn},primary_insurance_policy{id,insurer{id,attachments_allowed}},resubmission_code,payer_icn,original_form,upload_response}";

// Product.cpt_codes and Product.category are gated behind their flags to avoid
// querying a field the BE may not expose yet (deploy-ordering safety).
const getSaleItemsQuery = (
  multipleCptCodesFeature?: boolean,
  productCategories?: boolean
): string => {
  const cptCodes = multipleCptCodesFeature
    ? ",cpt_codes{id,code,description}"
    : "";
  const category = productCategories ? ",category" : "";
  return `{id,sort_order,discount_type{id,is_active,type,subtype,product{id,type,display_name,master_product{subtype}},mode,value,title},cpt_code{id,code,description},description,exchanged_at,returned_at,discount,insurer_responsibility,patient_responsibility,price,price_table_item{id,price,max_discount,is_active,price_table{id,name}},inventory_product{id,status,serial_number,condition,loanable,demoable,specification,manufacturer_warranty_expiration_date,available_quantity},patient_products{id,status,inventory_product{id,status,serial_number,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,condition,checked_in_at,ear,specification,is_active,quantity,ordered_quantity,reserved_quantity,loanable,demoable}},product{id,display_name,max_discount,has_picture${category},cpt_code{id,code,description}${cptCodes},master_product{id,type,subtype,specification}},quantity,quickbooks_line_id,tax_rate,taxable,total,insurers{amount,balance,id,content_type,insurer{id}},created_at,created_by,updated_by,delivered_at,order_item{id,patient_product{id,serial_number,ear},inventory_product{id,serial_number,product{id,display_name}}},order{id,type,clinic_order_number,status},claim_form_data,is_migrated}`;
};

// Dedicated read for the quote-conversion dialog (SN-19383). Separate from
// getSaleItemsQuery on purpose, for two reasons:
//
// 1. `requires_sourcing_decision` is only meaningful while converting a quote, and
//    it is not free on the backend — it walks master_product -> patient_products ->
//    latest status -> order_item, with prefetches keyed on the field being queried.
//    The ~13 other consumers of getSaleItemsQuery (invoice line items, summary
//    table, edit sale, refund line picker, ...) must not pay for it.
// 2. django-restql raises FieldNotFound and 400s the whole read for an unknown
//    field, so scoping the new field to this one query means a frontend-first
//    deploy degrades only the quote-conversion dialog instead of every sale-item
//    surface in the app. That is why this needs no feature flag.
//
// Trimmed to what the dialog actually renders: the sourcing flag, the line label,
// and enough of the quoted device's spec for the pills + the locked catalog field.
// Pricing, taxes, insurers, adjustments and QuickBooks fields are deliberately absent.
// `product.managed_stock` drives only the "Not stock-tracked" hint beside a row — it is
// NOT the exclude decision, which is always an explicit per-item choice (SN-19383).
const QUOTE_CONVERSION_ITEMS_QUERY =
  "{id,description,quantity,requires_sourcing_decision,inventory_product{id,ear,condition,specification},patient_products{id,inventory_product{id,ear,condition,specification}},product{id,display_name,max_discount,has_picture,managed_stock,master_product{id,type,subtype,specification}}}";

// DEV-3169. Dedicated read for the refit dialog's "What carries over" preview. Same precedent as
// QUOTE_CONVERSION_ITEMS_QUERY below: the sale-list queries select only a couple of item fields
// (PATIENT_SALES_QUERY asks for `items{cpt_code,insurer_responsibility}`), so a dialog handed a
// sale from a list has no price, quantity or description to show — it rendered "$NaN". Reading
// them here rather than widening the list query keeps the cost on the one dialog that needs it,
// and makes the dialog independent of whichever surface opened it (the action menu it now hangs
// off is used by several lists, each with a different projection).
//
// `returned_at` / `exchanged_at` are the point, not decoration: the backend copies only lines that
// carry neither (`billing/services/refit.py::rebillable_items`), because re-billing goods the
// patient gave back would put them on a claim the practice no longer supplied. The preview must
// apply the same filter or it promises lines the refit will not contain.
// `sort_order` and `total` are load-bearing, not extra: the preview must read like the invoice's
// own Line Items tab, and `price` is the UNIT price. A discounted line (unit $3,622.50 less a $500
// discount) bills $3,122.50, and a qty-2 line at $75.00 bills $150.00 — so a preview built on unit
// price shows numbers the claim will not carry. `total` is the line total the re-bill actually
// bills; `sort_order` is the order the invoice lists them in.
// `taxable` / `tax_rate` / `tax_amount` are here so the preview's arithmetic reconciles on screen.
// The server's `total` is `SaleItem.raw_total`, which INCLUDES tax on a taxable line; without a Tax
// column, Unit Price x Quantity would not add up to Line Total and the table would look wrong on
// every taxable row. They also let the client-side fallback reproduce `raw_total` rather than
// `total_without_tax`, so a line the server sent no total for is not quietly under-stated.
const REFIT_PREVIEW_ITEMS_QUERY =
  "{id,sort_order,description,quantity,price,discount,total,taxable,tax_rate,tax_amount,returned_at,exchanged_at,cpt_code{id,code}}";

// ENG-1834. Dedicated read for the invoice-delete repair check, used by the invoice action
// menu — the sale-list queries (SALES_DEFAULT_QUERY / PATIENT_SALES_QUERY) do not select
// `items{order,patient_products}`, so the menu has no way to see a repair link.
//
// Separate from getSaleItemsQuery on purpose, same precedent as QUOTE_CONVERSION_ITEMS_QUERY
// above: that query is ~13 consumers wide and returns pricing, taxes, insurers, discounts and
// QuickBooks fields this check has no use for, and scoping a read to one caller means a
// deploy-ordering problem degrades one dialog rather than every sale-item surface.
//
// `order.type` is the whole point (ORDER_REPAIR_TYPE); the patient products carry the device
// label and current status the warning dialog names. See utils/repair-invoice.util.ts.
const SALE_REPAIR_LINKS_QUERY =
  "{id,description,order{id,type},patient_products{id,status,inventory_product{id,serial_number,ear}}}";

export const STATEMENT_QUERY =
  "{id,appointment{type{name}},service_date,status,balance,patient_responsibility_total,insurer_responsibility_total,patient_responsibility_balance,insurer_responsibility_balance,hcfa1500_forms{id,uploaded_at,status,is_uploaded,primary_insurance_policy{id,insurer{id,name,attachments_allowed}}},adjustments{id,amount,description,adjustment_date,payer_type},items{id,billable,patient_responsibility,insurer_responsibility,description,total,taxable,tax_rate,quantity,price,patient_products{id,status},product{id,display_name,master_product{type,subtype}},cpt_code{id,code,description},exchanged_at,returned_at},refunds{id,refunded_at,payer_type,amount,description,reason,surcharge_amount},payments{id,amount,method,payer_type,payment_date,description,surcharge_amount},total,patient{id,first_name,last_name}}";

// Maps FE ordering fields that rely on queryset annotations (via NullsOrderingFilter)
// to valid DB fields for CSV exports, which bypass the filter and call order_by() directly.
const CSV_ORDERING_MAP: Record<string, string | null> = {
  patient_name: "patient__last_name",
  claim_status: null
};

const mapSaleCsvOrdering = (ordering?: string): string | undefined => {
  if (!ordering) return undefined;
  return (
    ordering
      .split(",")
      .map((field) => {
        const desc = field.startsWith("-");
        const name = desc ? field.slice(1) : field;
        const mapped = name in CSV_ORDERING_MAP ? CSV_ORDERING_MAP[name] : name;
        if (!mapped) return null;
        return desc ? `-${mapped}` : mapped;
      })
      .filter(Boolean)
      .join(",") || undefined
  );
};

export const getDefaultSalesFilters = (filters: SalesFilters) => {
  const { customFilters, baseFilter } = filters;

  return {
    id: filters.id,
    discarded: filters.discarded,
    before_service_date: filters.before_service_date,
    after_service_date: filters.after_service_date,
    // Sent instead of the service-date pair when the Sales report's date-range mode is set
    // to Created Date (features/reports/sales-date-range-modes).
    //
    // NOTE: this function is an explicit allowlist, not a spread — anything not named here
    // is silently dropped before the request is built, with no type error and no runtime
    // warning. That is why the Sales report needs this line while Line Items / Returned /
    // Exchanged do not: their services spread the whole filter object. Any new query param
    // for /sales/ has to be added here too.
    before_created_at: filters.before_created_at,
    after_created_at: filters.after_created_at,
    before_delivery_date: filters.before_delivery_date,
    after_delivery_date: filters.after_delivery_date,
    after_discarded_at: filters.after_discarded_at,
    before_discarded_at: filters.before_discarded_at,
    assigned_tags: customFilters?.assigned_tags,
    payment_source_type: customFilters?.payment_source_type,
    patient: customFilters?.patient,
    provider: customFilters?.provider,
    hcfa1500_form_status: customFilters?.hcfa1500FormStatus,
    status: customFilters?.status,
    clinic: customFilters?.clinic,
    region: customFilters?.region,
    sale_age: customFilters?.sale_age,
    taxable: customFilters?.taxable,
    product_type: customFilters?.product_type,
    product_subtype: customFilters?.product_subtype,
    category: customFilters?.category,
    category_isnull: customFilters?.category_isnull,
    is_returned: customFilters?.is_returned,
    service_date: customFilters?.service_date,
    ...filters.baseFilter,
    excluded_status: customFilters?.excluded_status
      ? customFilters?.excluded_status
      : baseFilter?.excluded_status || undefined,
    is_future_sale:
      filters.is_future_sale ?? (filters.baseFilter as any)?.is_future_sale,
    is_balanced:
      filters.is_balanced ?? (filters.baseFilter as any)?.is_balanced,
    exclude_future_sale:
      filters.exclude_future_sale ??
      (filters.baseFilter as any)?.exclude_future_sale,
    appointment: filters.appointment,
    has_negative_balance: filters.has_negative_balance,
    has_negative_balance_accounts_receivable:
      filters.has_negative_balance_accounts_receivable,
    has_patient_negative_balance: !isBlank(filters.has_patient_negative_balance)
      ? filters.has_patient_negative_balance
      : customFilters?.has_patient_negative_balance,
    has_outstanding_patient_balance:
      customFilters?.has_outstanding_patient_balance,
    has_outstanding_insurer_balance:
      customFilters?.has_outstanding_insurer_balance,
    query: filters.query ? filters.query : PATIENT_SALES_QUERY,
    ordering: filters.ordering || "-service_date,-pk",
    opening_balance: filters.opening_balance,
    search: filters.search && encodeURIComponent(filters.search),
    tag_description: filters.tag_description,
    snapshot: filters.snapshot,
    has_refunds: filters.has_refunds,
    nulls_first: filters.nulls_first,
    is_qb_synced: customFilters?.is_qb_synced,
    insurer: customFilters?.insurer,
    primary_insurer: customFilters?.primary_insurer,
    // SECONDARY_INSURER_BALANCE_FEATURE (backend-first, sent only while the flag
    // is on): the Insurer Receivable drill-down's responsible-payer filters. This
    // function is an allowlist (see the NOTE above) — without these lines the
    // params are silently dropped and the expander lists the whole practice.
    responsible_insurer: customFilters?.responsible_insurer,
    payer_position: customFilters?.payer_position,
    managed_care_plan: customFilters?.managed_care_plan,
    managed_care_provider: customFilters?.managed_care_provider,
    has_active_subscription: true,
    include_totals: filters?.include_totals,
    has_insurer_negative_balance: customFilters?.has_insurer_negative_balance,
    optimize_ar: filters.optimize_ar
  };
};

export const salesApi: any = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getSalesPaginated: createPaginatedEndpoint(builder, {
      queryFn: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          ...getDefaultSalesFilters(filters),
          limit: filters.limit ?? DEFAULT_SALES_LIMIT,
          offset: filters.offset ?? 0
        });

        return `/sales/?${filtersString}&nulls_first=true`;
      },
      providesTags: ["Paginated-Sales", "List-Paginated-Sales"],
      transformResponseFn: (
        response: SalesResponse | AccountReceivableSalesResponse
      ) =>
        ("data" in response ? response.data.results : response.results)?.map(
          (sale) => formatSale(sale)
        )
    }),
    querySales: builder.query<Sale[] | SalesReportResponse, SalesFilters>({
      query: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          ...getDefaultSalesFilters(filters),

          limit: filters.limit ?? DEFAULT_SALES_LIMIT,
          offset: filters.offset ?? 0
        });

        return `/sales/?${filtersString}&nulls_first=true`;
      },
      providesTags: ["Sales"],
      transformResponse: (
        response:
          | SalesResponse
          | AccountReceivableSalesResponse
          | SalesReportResponse
      ) => {
        if ("total" in response) {
          return response;
        }

        if ("data" in response) {
          return response.data.results?.map((sale) =>
            formatSale(sale, response.overview)
          );
        }

        return response.results?.map((sale) => formatSale(sale));
      }
    }),
    querySalesCounter: builder.query<
      SalesTotalResponse,
      Omit<SalesFilters, "query" | "limit" | "offset"> & {
        includeTotals?: boolean;
      }
    >({
      query: ({ includeTotals, ...filters }) =>
        `/sales/${
          includeTotals ? "total" : "count"
        }/?${formatValidQueryParamUrlCommaSeparated({
          ...filters.baseFilter
        })}`,
      transformResponse: (response: SalesTotalResponse | string, meta, arg) => {
        if (arg.includeTotals && typeof response === "object") {
          return response as SalesTotalResponse;
        } else {
          return {
            count: Number(response || 0),
            total_amount: undefined,
            total_balance: undefined,
            total_balance_of_uncompleted_sales: undefined
          };
        }
      },
      providesTags: ["Sales"]
    }),
    getPatientSales: builder.query<Sale[], PatientSalesFilters>({
      query: ({ query = PATIENT_SALES_QUERY, ...filters }) =>
        `/sales/?${formatValidQueryParamUrlCommaSeparated(
          filters
        )}&query=${query}&nulls_first=true`,
      providesTags: ["Sales"],
      transformResponse: (response: SalesResponse) =>
        response.results.map((sale) => formatSale(sale))
    }),
    getPatientSalesPaginated: createPaginatedEndpoint(builder, {
      queryFn: ({ query = PATIENT_SALES_QUERY, ...filters }) =>
        `/sales/?${formatValidQueryParamUrlCommaSeparated(
          filters
        )}&query=${query}&nulls_first=true`,
      providesTags: ["Paginated-Sales"],
      transformResponseFn: (response: SalesResponse) =>
        response.results.map((sale) => formatSale(sale))
    }),
    getSale: builder.query<
      Sale,
      { saleId: number; query?: string; timestamp?: string }
    >({
      query: ({ saleId, query = GET_SALE_QUERY }) =>
        `/sales/${saleId}/?query=${query}`,
      providesTags: ["Sales"],
      transformResponse: formatSale
    }),
    getSaleBalance: builder.query<
      {
        id: number;
        is_balanced: boolean;
        balance: string;
        service_date: string | null;
        status: number;
      },
      number
    >({
      query: (saleId) =>
        `/sales/${saleId}/?query={id,balance,is_balanced,service_date,status}`
    }),
    getSaleHistory: builder.query<
      InvoiceHistoryEvent[],
      { saleId: number; includeMovedAway?: boolean }
    >({
      query: ({ saleId, includeMovedAway }) =>
        `/sales/${saleId}/history/${includeMovedAway ? "?include_moved_away=true" : ""}`,
      // Refetch when any invoice child record changes so the audit timeline stays current.
      providesTags: [
        "Sales",
        "Payments",
        "Adjustments",
        "SaleItems",
        "Sales Refunds"
      ]
    }),
    getSaleItems: builder.query<
      SaleItem[],
      {
        saleId: number;
        multipleCptCodesFeature?: boolean;
        productCategories?: boolean;
      }
    >({
      query: ({ saleId, multipleCptCodesFeature, productCategories }) => {
        if (!Number.isFinite(saleId) || saleId <= 0) {
          throw new Error(`getSaleItems: invalid saleId ${saleId}`);
        }
        return `/sales/${saleId}/items/?query=${getSaleItemsQuery(multipleCptCodesFeature, productCategories)}`;
      },
      providesTags: ["SaleItems"]
    }),
    // Sale items as the quote-conversion dialog needs them — carries
    // requires_sourcing_decision, which no other surface reads (SN-19383).
    getQuoteConversionItems: builder.query<SaleItem[], { saleId: number }>({
      query: ({ saleId }) => {
        if (!Number.isFinite(saleId) || saleId <= 0) {
          throw new Error(`getQuoteConversionItems: invalid saleId ${saleId}`);
        }
        return `/sales/${saleId}/items/?query=${QUOTE_CONVERSION_ITEMS_QUERY}`;
      },
      providesTags: ["SaleItems"]
    }),
    // DEV-3169: line-item preview for the refit dialog. Narrow on purpose — see
    // REFIT_PREVIEW_ITEMS_QUERY.
    getRefitPreviewItems: builder.query<SaleItem[], { saleId: number }>({
      query: ({ saleId }) => {
        if (!Number.isFinite(saleId) || saleId <= 0) {
          throw new Error(`getRefitPreviewItems: invalid saleId ${saleId}`);
        }
        return `/sales/${saleId}/items/?query=${REFIT_PREVIEW_ITEMS_QUERY}`;
      },
      providesTags: ["SaleItems"]
    }),
    // ENG-1834: pre-flight for the invoice-delete repair warning. See
    // SALE_REPAIR_LINKS_QUERY above for why this is not getSaleItems.
    getSaleRepairLinks: builder.query<SaleItem[], { saleId: number }>({
      query: ({ saleId }) => {
        if (!Number.isFinite(saleId) || saleId <= 0) {
          throw new Error(`getSaleRepairLinks: invalid saleId ${saleId}`);
        }
        return `/sales/${saleId}/items/?query=${SALE_REPAIR_LINKS_QUERY}`;
      },
      providesTags: ["SaleItems"]
    }),
    getSaleItemCosts: builder.query<
      {
        id: number | string;
        cost?: string;
        inventory_product?: { id: number; supplier_price?: string } | null;
      }[],
      { saleId: number }
    >({
      query: ({ saleId }) =>
        `/sales/${saleId}/items/?query={id,cost,inventory_product{id,supplier_price}}`,
      providesTags: ["SaleItemCosts"]
    }),
    getHCFAForms: builder.query<
      HCFAForm[],
      { saleId: number; box22Enabled?: boolean }
    >({
      query: ({ saleId, box22Enabled }) =>
        `/sales/${saleId}/hcfa1500-forms/?query=${
          box22Enabled
            ? GET_HCFA1500_FORMS_BOX22_QUERY
            : GET_HCFA1500_FORMS_QUERY
        }`,
      providesTags: ["HCFA1500Forms"]
    }),
    addSale: builder.mutation<Sale, SaleInput>({
      query: (sale) => ({
        url: "/sales/",
        method: "POST",
        body: sale
      }),
      invalidatesTags: ["Sales", "Patient", "Patients", "PatientProducts"]
    }),
    updateSale: builder.mutation<Sale, Partial<SaleInput>>({
      query: ({ id, ...data }) => ({
        url: `/sales/${id}/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["Sales", "Patient", "Patients", "PatientProducts"]
    }),
    // SECONDARY_INSURER_BALANCE_FEATURE (backend-first): moves the invoice's
    // outstanding insurer balance between payers — 'secondary' stamps
    // insurer_balance_moved_to_secondary_at, 'primary' clears it, 'patient'
    // transfers the remainder onto the patient (same sweep as
    // assign_remaining_responsibility). SaleItems invalidated because the
    // 'patient' destination rewrites per-line responsibilities.
    moveInsurerBalance: builder.mutation<
      { id: number; insurer_balance_moved_to_secondary_at: string | null },
      { saleId: number; destination: "secondary" | "primary" | "patient" }
    >({
      query: ({ saleId, destination }) => ({
        url: `/sales/${saleId}/move-insurer-balance/`,
        method: "POST",
        body: { destination }
      }),
      invalidatesTags: ["Sales", "SaleItems"]
    }),
    // REFIT_REBILLING_FEATURE (backend-first, DEV-3169): re-bills an invoice on a new
    // date of service. The encounter fields are REQUIRED and are never inherited from the
    // original — they go on the new claim, so they have to be the visit that actually
    // happened (usually a different clinic from the original fitting). The backend also
    // requires practice.enable_refit_rebilling, so a 400 here can mean "not enabled for
    // this practice" as well as bad input.
    //
    // Invalidates Sales/SaleItems (a second invoice now exists for the patient) and
    // Patient/Patients (the chart's invoice list and balances). Revenue reports are NOT
    // invalidated: the refit is excluded from them by construction, so none of their
    // numbers move.
    createRefitInvoice: builder.mutation<
      CreateRefitInvoiceResponse,
      CreateRefitInvoiceInput
    >({
      query: ({ saleId, ...body }) => ({
        url: `/sales/${saleId}/create-refit/`,
        method: "POST",
        body
      }),
      invalidatesTags: [
        "Sales",
        "Paginated-Sales",
        "SaleItems",
        "Patient",
        "Patients"
      ]
    }),
    // Converts a Quote sale into a real invoice: applies per-device From Stock /
    // Place Order decisions, sets the service date from the selected appointment
    // and transitions the status — atomically on the backend (SN-18215).
    convertQuoteSale: builder.mutation<Sale, ConvertQuoteSaleInput>({
      query: ({ id, ...data }) => ({
        url: `/sales/${id}/convert-quote/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: [
        "Sales",
        "Paginated-Sales",
        "SaleItems",
        "PatientProducts",
        "Orders",
        "Patient",
        "Patients"
      ]
    }),
    deleteSale: builder.mutation<Sale, number>({
      query: (saleId) => ({
        url: `/sales/${saleId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Sales", "Patient", "Patients", "PatientProducts"]
    }),
    fileSaleHCFA: builder.mutation<HCFAForm, HCFAFormInput>({
      query: ({ saleId, ...hcfaFormFData }) => ({
        url: `/sales/${saleId}/hcfa1500-forms/`,
        method: "POST",
        body: hcfaFormFData
      }),
      invalidatesTags: ["Sales", "HCFA1500Forms"]
    }),
    updateHCFAFileSale: builder.mutation<HCFAForm, HCFAFormInput>({
      query: ({ id, saleId, ...hcfaFormFData }) => ({
        url: `/sales/${saleId}/hcfa1500-forms/${id}/`,
        method: "PATCH",
        body: hcfaFormFData
      }),
      invalidatesTags: ["Sales", "HCFA1500Forms"]
    }),
    deleteHCFAFileSale: builder.mutation<void, { saleId: number; id: number }>({
      query: ({ saleId, id }) => ({
        url: `/sales/${saleId}/hcfa1500-forms/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Sales", "HCFA1500Forms"]
    }),
    uploadHCFAAttachment: builder.mutation<HCFAForm, HCFAAttachmentInput>({
      query: ({ id, saleId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/sales/${saleId}/hcfa1500-forms/${id}/upload-attachment/`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["Sales", "HCFA1500Forms"]
    }),
    addSaleItem: builder.mutation<LineItem, ItemInput>({
      query: ({
        saleId,
        item: { id, ...itemData },
        deductInsuranceFromPatientResp
      }) => ({
        url: `/sales/${saleId}/items/`,
        method: "POST",
        body: deductInsuranceFromPatientResp
          ? { ...itemData, apply_insurer_floor: true }
          : itemData
      }),
      invalidatesTags: [
        "Sales",
        "SaleItems",
        "Patient",
        "Patients",
        "MergeTagsSale",
        "PatientProducts"
      ]
    }),
    updateSaleItem: builder.mutation<LineItem, ItemInput>({
      query: ({
        saleId,
        item: { id, ...itemData },
        deductInsuranceFromPatientResp
      }) => ({
        url: `/sales/${saleId}/items/${id}/`,
        method: "PATCH",
        body: deductInsuranceFromPatientResp
          ? { ...itemData, apply_insurer_floor: true }
          : itemData
      }),
      invalidatesTags: [
        "Sales",
        "SaleItems",
        "Patient",
        "Patients",
        "MergeTagsSale",
        "PatientProducts"
      ]
    }),
    deleteSaleItem: builder.mutation<LineItem, ItemInput>({
      query: ({ saleId, item }) => ({
        url: `/sales/${saleId}/items/${item.id}/`,
        method: "DELETE",
        body: item
      }),
      invalidatesTags: [
        "Sales",
        "Patient",
        "Patients",
        "SaleItems",
        "MergeTagsSale",
        "PatientProducts"
      ]
    }),
    postQuickbooksInvoice: builder.mutation<any, number>({
      query: (saleId) => ({
        url: `/sales/${saleId}/sync/`,
        method: "POST"
      }),
      invalidatesTags: ["Sales", "SaleItems"]
    }),
    postQuickbooksLineItemsSync: builder.mutation<any, number>({
      query: (saleId) => ({
        url: `/sales/${saleId}/quickbooks-sync/`,
        method: "POST"
      }),
      invalidatesTags: ["Sales", "SaleItems"]
    }),
    downloadSaleCSV: builder.query<CSVReportResponse, SalesDownloadQuery>({
      query: (filters) => ({
        url: `/sales/`,
        headers: { Accept: "custom-csv/json" },
        params: {
          ...filters,
          ordering: mapSaleCsvOrdering(filters?.ordering) || "-service_date,-pk"
        }
      })
    }),
    downloadAdpXml: builder.query<void, { saleId: number; fileName: string }>({
      query: ({ saleId }) => ({
        url: `/sales/${saleId}/adp/`,
        headers: {
          "Content-Type": "application/xml"
        },
        responseHandler: async (response: any) => {
          return await response.text();
        }
      }),
      transformResponse: (xmlText: string, meta, { fileName }) => {
        const downloadLink = document.createElement("a");
        downloadLink.setAttribute("target", "_blank");
        downloadLink.href = URL.createObjectURL(
          new Blob([xmlText], { type: "application/xml" })
        );

        downloadLink.download = fileName;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    }),
    markSalesAsCompleted: builder.mutation<
      void,
      { before_service_date: string; ignore_balance?: boolean }
    >({
      query: (params) => ({
        url: `/sales/mark-as-completed/`,
        method: "POST",
        params
      }),
      invalidatesTags: ["Sales", "Patient", "Patients"]
    }),
    moveSaleItem: builder.mutation<
      LineItem,
      { saleItemId: number; saleId: number }
    >({
      query: ({ saleItemId, saleId }) => ({
        url: `/sale-items/${saleItemId}/`,
        method: "PATCH",
        body: { sale: saleId }
      }),
      invalidatesTags: [
        "Sales",
        "SaleItems",
        "Patient",
        "Patients",
        "MergeTagsSale",
        "PatientProducts"
      ]
    }),
    getSaleQuickbooksSync: builder.query<
      { id: number; quickbooks_sync: { sync_status: string } },
      number
    >({
      query: (saleId) =>
        `/sales/${saleId}/?query={id,quickbooks_invoice_id,quickbooks_sync{sync_status,error}}`,
      providesTags: ["Sales", "SalesQuickBooksSync"]
    }),
    linkQuickBooksInvoice: builder.mutation<
      { status: string; quickbooks_invoice_id: string },
      { saleId: number; quickbooks_invoice_id: string }
    >({
      query: ({ saleId, quickbooks_invoice_id }) => ({
        url: `/sales/${saleId}/link-quickbooks-invoice/`,
        method: "POST",
        body: { quickbooks_invoice_id }
      }),
      invalidatesTags: ["Sales", "SalesQuickBooksSync", "Paginated-Sales"]
    }),
    sendToDarwill: builder.mutation<
      { status: string; created_count: number },
      {
        sale_ids: number[];
        invoice_memo?: string;
        include_icd10_codes?: boolean;
        bill_to_responsible_party?: boolean;
        billing_address_clinic?: number;
        include_qr_code?: boolean;
        include_sale_notes?: boolean;
        show_patient_credit?: boolean;
        invoice_payment_terms_days?: number | null;
      }
    >({
      query: (data) => ({
        url: `/sales/send-to-darwill/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Sales", "Paginated-Sales"]
    }),
    undiscardSale: builder.mutation<
      { id: number; restored: boolean; warnings: string[] },
      number
    >({
      query: (id) => ({ url: `/sales/${id}/undiscard/`, method: "POST" }),
      invalidatesTags: [
        "Sales",
        "Paginated-Sales",
        "Patient",
        "Patients",
        "Adjustments",
        "Refunds Report"
      ]
    }),
    undiscardRecord: builder.mutation<
      { id: number; type: string; restored: boolean },
      { type: string; id: number }
    >({
      query: ({ type, id }) => ({
        url: `/sales/undiscard-record/`,
        method: "POST",
        body: { type, id }
      }),
      invalidatesTags: [
        "Sales",
        "Paginated-Sales",
        "Patient",
        "Patients",
        "Adjustments",
        "Refunds Report"
      ]
    }),
    bulkSendToCollections: builder.mutation<
      {
        enrolled_count: number;
        skipped_invalid_status: number;
        skipped_no_balance: number;
        skipped_already_enrolled: number;
        error_count: number;
        job_id?: string | null;
      },
      | { sale_ids: number[]; cadence?: number }
      | { filters: Record<string, unknown>; cadence?: number }
    >({
      query: (body) => ({
        url: `/collections/bulk-create/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["Sales", "Paginated-Sales", "Collections"]
    }),
    getBulkEnrollJob: builder.query<
      {
        done: boolean;
        total: number;
        enrolled_count: number;
        failed: { sale_id: number; patient_name: string | null }[];
      },
      string
    >({
      query: (jobId) => `/collections/bulk-create/${jobId}/`,
      keepUnusedDataFor: 0
    })
  })
});

export const {
  useQuerySalesQuery,
  useGetBulkEnrollJobQuery,
  useLazyQuerySalesQuery,
  useGetSalesPaginatedQuery,
  useLazyDownloadSaleCSVQuery,
  useLazyQuerySalesCounterQuery,
  useGetPatientSalesQuery,
  useGetSaleItemsQuery,
  useGetRefitPreviewItemsQuery,
  useGetQuoteConversionItemsQuery,
  useLazyGetSaleItemsQuery,
  useLazyGetSaleRepairLinksQuery,
  useGetHCFAFormsQuery,
  useAddSaleMutation,
  useUpdateSaleMutation,
  useMoveInsurerBalanceMutation,
  useCreateRefitInvoiceMutation,
  useConvertQuoteSaleMutation,
  useDeleteSaleMutation,
  useGetSaleQuery,
  useLazyGetSaleQuery,
  useLazyGetSaleBalanceQuery,
  useGetSaleHistoryQuery,
  useFileSaleHCFAMutation,
  useUpdateHCFAFileSaleMutation,
  useDeleteHCFAFileSaleMutation,
  useUploadHCFAAttachmentMutation,
  useAddSaleItemMutation,
  useUpdateSaleItemMutation,
  useDeleteSaleItemMutation,
  usePostQuickbooksLineItemsSyncMutation,
  usePostQuickbooksInvoiceMutation,
  useLazyDownloadAdpXmlQuery,
  useMarkSalesAsCompletedMutation,
  useMoveSaleItemMutation,
  useGetSaleQuickbooksSyncQuery,
  useLazyGetSaleQuickbooksSyncQuery,
  useLinkQuickBooksInvoiceMutation,
  useSendToDarwillMutation,
  useBulkSendToCollectionsMutation,
  useUndiscardSaleMutation,
  useUndiscardRecordMutation,
  useLazyGetSaleItemCostsQuery
} = salesApi;
